/**
 * iter240 — Bloc « Messages client » sur la fiche OR (côté garage).
 * Même fil que celui visible par le client sur son lien de suivi / le devis.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("fr-CH", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
};

export const OrMessagesClientBlock = ({ orId }) => {
  const [items, setItems] = useState([]);
  const [texte, setTexte] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const fin = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/or/${orId}/messages`);
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [orId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { fin.current?.scrollIntoView({ block: "nearest" }); }, [items]);

  const send = async () => {
    if (!texte.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/or/${orId}/messages`, { texte: texte.trim() });
      setItems((prev) => [...prev, data]);
      setTexte("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4 border-gray-200" data-testid="or-messages-client-block">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="w-4 h-4 text-[#52a675]" />
        <h3 className="font-heading font-semibold text-sm">Messages client</h3>
        <span className="text-[11px] text-gray-400">
          {items.length === 0 ? "aucun message" : `${items.length} message${items.length > 1 ? "s" : ""}`}
        </span>
      </div>

      {loading ? (
        <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-gray-400" /></div>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="or-messages-list">
          {items.length === 0 ? (
            <p className="text-xs text-gray-400">
              Le client peut écrire ici depuis son lien de suivi ou la page du devis. Vous pouvez aussi
              lancer la discussion.
            </p>
          ) : (
            items.map((m) => {
              const garage = m.auteur === "garage";
              return (
                <div key={m.id} className={`flex ${garage ? "justify-end" : "justify-start"}`} data-testid="or-message-item">
                  <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${garage ? "bg-[#52a675] text-white" : "bg-gray-100 text-gray-800"}`}>
                    {!garage && <div className="text-[10px] font-semibold opacity-70 mb-0.5">{m.auteur_nom}</div>}
                    {m.texte && <div className="whitespace-pre-wrap break-words">{m.texte}</div>}
                    {m.a_photo && (
                      <a href={`${API_BASE}/api/or/messages/${m.id}/photo`} target="_blank" rel="noreferrer">
                        <img
                          src={`${API_BASE}/api/or/messages/${m.id}/photo`}
                          alt="Photo du client"
                          className="mt-1 rounded-lg max-h-40 object-cover"
                          data-testid={`or-message-photo-${m.id}`}
                        />
                      </a>
                    )}
                    <div className={`text-[10px] mt-0.5 ${garage ? "text-white/70" : "text-gray-400"}`}>{fmt(m.created_at)}</div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={fin} />
        </div>
      )}

      <div className="flex items-end gap-2 mt-3">
        <textarea
          value={texte}
          onChange={(e) => setTexte(e.target.value)}
          placeholder="Répondre au client…"
          rows={2}
          className="flex-1 min-w-0 rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-gray-300"
          data-testid="or-message-input"
        />
        <Button
          onClick={send}
          disabled={busy || !texte.trim()}
          className="bg-[#52a675] hover:bg-[#3d8a5c] flex-shrink-0"
          data-testid="or-message-send"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </Card>
  );
};

export default OrMessagesClientBlock;
